/* components/reports/FoodcostOptimizationReport.jsx — единый рендерер отчёта
   «FC-оптимизация» v2 (Report.result: _schema:'f40', schema_version:2).
   Используется и детальной страницей (/reports/:id), и инлайн-канвасом чата
   (FoodcostCanvas) — зеркало AbcAnalysisReport / InventoryAnalysisReport.

   Структура зеркалит продуктовый промпт («Анализ калькуляционных карт и
   оптимизация food cost»):
     1) метрики (себестоимость/цена/FC/цель/разрыв) и исходные ограничения;
     2) данные карт (файлы, дерево блюдо→п/ф, предупреждения парсера);
     3) разбор блюда: драйверы себестоимости через цепочку п/ф + состав;
     4) допустимые сценарии (запрещённые ограничениями — с причиной);
     5) рекомендации — кликабельные теги типов мер и риска фильтруют список;
     6) лучшие варианты (безопасный/компромисс/максимум);
     7) финальный расчёт (таблица мер + новая себестоимость/FC/цель) и итог.

   Все деньги/проценты посчитаны кодом на бэке (reports/foodcost_calc.py);
   compact-режим — для канваса в чате (меньше секций и строк). */

import React from 'react';
import { Icon } from '../ui/Icon.jsx';
import { AiSkippedNote } from './AiSkippedNote.jsx';
import { fmt } from '../../lib/format.js';
import {
  FC_CONSTRAINT_LABEL, FC_CONSTRAINT_VALUE, FC_GOAL, FC_MEASURE_ORDER,
  FC_RISK_ORDER, FC_SCENARIO, FC_VARIANT_ORDER,
  fcMeasure, fcRisk, fcVariant,
} from '../../lib/reports/foodcostMeta.js';

const { useMemo, useState } = React;

const money = (v) => (v == null ? '—' : fmt.money(v));
const pct1 = (v) => (v == null ? '—' : fmt.percent(v, { digits: 1 }));
const grams = (v) => (v == null ? '—'
  : `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v)} г`);

function Sec({ title, hint, children }) {
  return (
    <section className="canvas-sec">
      {title && (
        <div className="canvas-sec__head">
          <h4 className="canvas-sec__title t-eyebrow">{title}</h4>
          {hint && <span className="canvas-sec__hint t-caption text-mute">{hint}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

/* Кликабельный тег-фильтр (тип меры или риск) — паттерн AbcAnalysisReport. */
function FilterTag({ meta, active, onToggle, count, compactLabel }) {
  return (
    <button
      type="button"
      className={`tagpill tagpill--${meta.tone} cv-tagbtn${active ? ' is-active' : ''}`}
      title={meta.title}
      aria-pressed={active}
      onClick={onToggle}
    >
      <Icon name={meta.icon} size={12} />
      {compactLabel ? meta.short : meta.label}
      {count != null && <span className="num cv-tagbtn__n">{count}</span>}
    </button>
  );
}

/* ── 1. исходные ограничения ── */
function ConstraintsPanel({ c, compact }) {
  if (!c) return null;
  const goal = c.goal || {};
  const goalMeta = goal.kind ? FC_GOAL[goal.kind] : null;
  return (
    <Sec
      title="Исходные ограничения"
      hint={c.assumed ? 'часть рамок принята допущением — уточните в чате, и отчёт пересоберём' : null}
    >
      <div className="cv-chips fc2-constraints">
        {Object.entries(FC_CONSTRAINT_LABEL).map(([key, label]) => {
          const v = FC_CONSTRAINT_VALUE[c[key]] || FC_CONSTRAINT_VALUE.unknown;
          return (
            <span className={`tagpill tagpill--${v.tone}`} key={key}>
              {label}: {v.label}
            </span>
          );
        })}
        {(c.untouchable || []).map((u, i) => (
          <span className="tagpill tagpill--bad" key={'u' + i} title="Не трогаем по требованию пользователя">
            <Icon name="lock" size={12} /> {u}
          </span>
        ))}
        {goalMeta && (
          <span className="tagpill tagpill--accent" title="Цель оптимизации">
            <Icon name="sparkles" size={12} />
            {goalMeta.label}{goal.value != null ? ` ${goalMeta.fmt(goal.value)}` : ''}
          </span>
        )}
        {c.assumed && (
          <span className="tagpill tagpill--warn" title="Ограничения не были заданы полностью — приняты осторожные допущения">
            <Icon name="info" size={12} /> допущения
          </span>
        )}
      </div>
      {!compact && c.notes && <p className="t-caption text-mute fc2-note">{c.notes}</p>}
    </Sec>
  );
}

/* ── 2. данные карт ── */
function CardsPanel({ meta, compact }) {
  if (!meta) return null;
  const cards = meta.cards || [];
  const warnings = meta.warnings || [];
  if (compact && !warnings.length) return null;
  return (
    <Sec title="Данные калькуляционных карт">
      <div className="inv2-valid">
        {!compact && cards.map((c, i) => (
          <div className="inv2-file" key={i}>
            <Icon name={c.is_dish ? 'utensils' : 'box'} size={14} />
            <span className="cv-strong truncate">{c.name || c.filename || `Карта #${i + 1}`}</span>
            <span className="text-mute t-caption">
              {c.is_dish ? 'блюдо' : 'п/ф'} · {c.rows_parsed} строк
              {c.output_kg != null ? ` · выход ${c.output_kg} кг` : ''}
              {c.parse_method === 'llm' ? ' · распознано ИИ' : ''}
              {c.parse_method === 'db' ? ' · из базы' : ''}
              {c.used === false ? ' · не использована' : ''}
            </span>
          </div>
        ))}
        {warnings.length > 0 && (
          <div className="callout callout--warn inv2-callout" role="note">
            <Icon name="alert-triangle" size={15} />
            <div>{warnings.map((s, i) => <div key={i}>{s}</div>)}</div>
          </div>
        )}
      </div>
    </Sec>
  );
}

/* ── 3. драйверы себестоимости (через цепочку п/ф) ── */
function Drivers({ list = [], compact }) {
  const shown = compact ? list.slice(0, 6) : list;
  const max = Math.max(1, ...shown.map((d) => d.share_pct || 0));
  if (!shown.length) return <p className="t-caption text-mute">Нет данных по строкам.</p>;
  return (
    <div className="cv-drivers">
      {shown.map((d, i) => (
        <div className="cv-driver" key={i} title={d.path && d.path.length ? `Через: ${d.path.join(' → ')}` : undefined}>
          <span className="cv-driver__name truncate">
            {d.name}
            {d.path && d.path.length > 0 && (
              <span className="text-mute t-caption fc2-path"> · {d.path[d.path.length - 1]}</span>
            )}
          </span>
          <span className="cv-driver__bar">
            <span className="cv-driver__fill" style={{ width: ((d.share_pct || 0) / max * 100) + '%' }} />
          </span>
          <span className="cv-driver__val num">
            {money(d.cost_rub)}{d.share_pct != null ? ` · ${fmt.percent(d.share_pct)}` : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

/* состав блюда — строки карты верхнего уровня */
function ComponentsTable({ rows = [] }) {
  if (!rows.length) return null;
  return (
    <div className="cv-table-wrap fc2-wrap">
      <table className="cv-table inv2-table fc2-table">
        <thead>
          <tr>
            <th>Компонент</th><th className="num">Закладка</th>
            <th className="num">Цена, ₽/кг</th><th className="num">В порции, ₽</th>
            <th className="num">Доля</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td data-label="Компонент">
                <span className="cv-strong">{r.name}</span>
                {r.is_pf && <span className="tagpill tagpill--neutral fc2-pf-chip">п/ф</span>}
              </td>
              <td className="num" data-label="Закладка">{grams(r.qty_g)}</td>
              <td className="num" data-label="Цена">{r.price_rub_per_kg == null ? '—' : fmt.money(r.price_rub_per_kg)}</td>
              <td className="num" data-label="В порции">{money(r.cost_rub)}</td>
              <td className="num" data-label="Доля">{r.share_pct == null ? '—' : fmt.percent(r.share_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── 4. допустимые сценарии ── */
function Scenarios({ list = [] }) {
  if (!list.length) return null;
  return (
    <div className="cv-recs fc2-scenarios">
      {list.map((s) => {
        const meta = FC_SCENARIO[s.id] || { label: s.id, icon: 'circle' };
        return (
          <div className={'cv-rec' + (s.allowed ? '' : ' fc2-rec--blocked')} key={s.id}>
            <span className="cv-rec__label">
              <Icon name={meta.icon} size={13} /> {meta.label}
            </span>
            {s.allowed ? (
              <span className="cv-rec__save num">
                {s.required_savings_rub != null ? `−${fmt.money(s.required_savings_rub)}`
                  : s.required_new_price_rub != null ? `до ${fmt.money(s.required_new_price_rub)}` : 'доступен'}
              </span>
            ) : (
              <span className="cv-rec__save fc2-blocked-label"><Icon name="lock" size={12} /> недоступен</span>
            )}
            <span className="cv-rec__meas t-caption text-mute">{s.allowed ? s.note : (s.blocked_by || s.note)}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── 5. карточка рекомендации ── */
function RecCard({ rec, filter, onToggle, dimmed }) {
  const m = fcMeasure(rec.measure_type);
  const r = fcRisk(rec.risk);
  const range = rec.savings_min_rub != null && rec.savings_max_rub != null
    && rec.savings_max_rub > (rec.savings_rub || 0);
  return (
    <div className={'cv-measure' + (dimmed ? ' fc2-rec--dim' : '')} id={`fc2-rec-${rec.id}`}>
      <div className="cv-measure__head">
        <span className="cv-measure__type t-eyebrow num">{rec.id}</span>
        <FilterTag
          meta={m} compactLabel
          active={!!filter && filter.kind === 'measure' && filter.id === rec.measure_type}
          onToggle={() => onToggle('measure', rec.measure_type)}
        />
        <FilterTag
          meta={r} compactLabel
          active={!!filter && filter.kind === 'risk' && filter.id === rec.risk}
          onToggle={() => onToggle('risk', rec.risk)}
        />
      </div>
      <p className="cv-measure__desc">{rec.title}</p>
      <div className="cv-measure__delta">
        {(rec.before_text || rec.after_text) && (
          <span className="cv-measure__pair">
            {rec.before_text} <Icon name="arrow-right" size={12} />{' '}
            <span className="cv-measure__to">{rec.after_text}</span>
          </span>
        )}
        <span className="cv-measure__pair">
          Экономия:{' '}
          <span className="num cv-measure__to">−{money(rec.savings_rub)}</span>
          {rec.savings_pct != null && <span className="text-mute"> · {fmt.percent(rec.savings_pct)}</span>}
          {range && (
            <span className="text-mute t-caption" title="Оценочный диапазон; в расчётах — нижняя граница">
              {' '}(оценка {fmt.money(rec.savings_min_rub)}–{fmt.money(rec.savings_max_rub)})
            </span>
          )}
          {rec.new_price_rub != null && (
            <span className="text-mute"> · новая цена {fmt.money(rec.new_price_rub)}</span>
          )}
        </span>
      </div>
      {rec.compensation && <p className="cv-measure__notes text-mute">Компенсация: {rec.compensation}</p>}
      {rec.justification && <p className="cv-measure__notes text-mute">{rec.justification}</p>}
    </div>
  );
}

/* ── 6. лучшие варианты ── */
function Variants({ variants, recs, picked }) {
  if (!variants) return null;
  const byId = {};
  (recs || []).forEach((r) => { byId[r.id] = r; });
  const ids = FC_VARIANT_ORDER.filter((id) => variants[id]);
  if (!ids.length) return null;
  return (
    <div className="cv-recs">
      {ids.map((id) => {
        const v = variants[id];
        const meta = fcVariant(id);
        const risk = fcRisk(v.risk);
        const names = (v.rec_ids || []).map((rid) => (byId[rid] ? byId[rid].title : rid));
        return (
          <div className={'cv-rec' + (picked === id ? ' cv-rec--pick' : '')} key={id} title={meta.title}>
            <span className="cv-rec__label"><Icon name={meta.icon} size={13} /> {meta.label}</span>
            <span className="cv-rec__save num">−{money(v.savings_rub)}</span>
            <span className="cv-rec__meas t-caption text-mute">
              себестоимость {money(v.new_cost_rub)}
              {v.new_fc_pct != null ? ` · FC ${fmt.percent(v.new_fc_pct, { digits: 1 })}` : ''}
              {' · '}
              <span className={`tagpill tagpill--${risk.tone} fc2-pf-chip`}>{risk.short}</span>
              {v.goal_achieved != null && (
                <span className={'fc2-goal ' + (v.goal_achieved ? 'fc2-goal--ok' : 'fc2-goal--bad')}>
                  {' '}{v.goal_achieved ? '✓ цель достигнута' : '✗ цель не достигнута'}
                </span>
              )}
            </span>
            {v.rationale && <span className="cv-rec__meas t-caption text-mute">{v.rationale}</span>}
            {names.length > 0 && (
              <span className="cv-rec__meas t-caption text-mute">{names.join(' + ')}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── 7. финальный расчёт ── */
function FinalTable({ final, breakdown }) {
  if (!final) return null;
  const scrollTo = (recId) => {
    const el = document.getElementById(`fc2-rec-${recId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const vMeta = fcVariant(final.variant);
  return (
    <>
      <p className="t-caption text-mute fc2-note">
        Расчёт по варианту «{vMeta.label}» — клик по мере ведёт к её карточке.
      </p>
      <div className="cv-table-wrap fc2-wrap">
        <table className="cv-table inv2-table fc2-table">
          <thead>
            <tr><th>Мера</th><th className="num">Экономия, ₽</th><th className="num">Экономия, %</th><th>Комментарий</th></tr>
          </thead>
          <tbody>
            {(final.rows || []).map((row) => (
              <tr key={row.rec_id} className="fc2-final-row" onClick={() => scrollTo(row.rec_id)}>
                <td data-label="Мера"><span className="cv-strong">{row.title}</span></td>
                <td className="num" data-label="Экономия, ₽">−{money(row.savings_rub)}</td>
                <td className="num" data-label="Экономия, %">{row.savings_pct == null ? '—' : fmt.percent(row.savings_pct)}</td>
                <td data-label="Комментарий" className="cv-comment"><span className="text-dim">{row.comment || ''}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="canvas-metrics fc2-final-metrics" style={{ '--cm-count': 4 }}>
        <div className="canvas-metric canvas-metric--ok">
          <span className="canvas-metric__val num">−{money(final.total_savings_rub)}</span>
          <span className="canvas-metric__lbl t-caption">Суммарная экономия</span>
        </div>
        <div className="canvas-metric">
          <span className="canvas-metric__val num">{money(final.new_cost_rub)}</span>
          <span className="canvas-metric__lbl t-caption">Новая себестоимость</span>
        </div>
        <div className={'canvas-metric' + (
          final.new_fc_pct == null || breakdown.target_fc_pct == null ? ''
            : final.new_fc_pct <= breakdown.target_fc_pct ? ' canvas-metric--ok' : ' canvas-metric--warn')}
        >
          <span className="canvas-metric__val num">
            {final.new_fc_pct != null ? fmt.percent(final.new_fc_pct, { digits: 1 }) : '—'}
          </span>
          <span className="canvas-metric__lbl t-caption">
            Новый food cost{final.new_price_rub != null ? ` (цена ${fmt.money(final.new_price_rub)})` : ''}
          </span>
        </div>
        <div className={'canvas-metric' + (final.goal_achieved == null ? '' : final.goal_achieved ? ' canvas-metric--ok' : ' canvas-metric--bad')}>
          <span className="canvas-metric__val num">
            {final.goal_achieved == null ? '—' : final.goal_achieved ? '✓ да' : '✗ нет'}
          </span>
          <span className="canvas-metric__lbl t-caption">Цель достигнута</span>
        </div>
      </div>
      {final.margin_delta_rub != null && (
        <p className="t-caption text-mute fc2-note">
          Маржа на порцию: {money(final.margin_now_rub)} → {money(final.margin_new_rub)}{' '}
          ({final.margin_delta_rub >= 0 ? '+' : ''}{fmt.money(final.margin_delta_rub)}).
        </p>
      )}
    </>
  );
}

function FoodcostOptimizationReport({ r, compact = false }) {
  // единый фильтр рекомендаций: {kind:'measure'|'risk', id} | null; клик по
  // тому же тегу снимает фильтр (зеркало AbcAnalysisReport.filter)
  const [filter, setFilter] = useState(null);
  const toggle = (kind, id) => setFilter(
    (cur) => (cur && cur.kind === kind && cur.id === id ? null : { kind, id }),
  );

  const bd = r.breakdown || {};
  const recs = r.recommendations || [];
  const counts = useMemo(() => {
    const m = {}; const rk = {};
    recs.forEach((rec) => {
      m[rec.measure_type] = (m[rec.measure_type] || 0) + 1;
      rk[rec.risk] = (rk[rec.risk] || 0) + 1;
    });
    return { measure: m, risk: rk };
  }, [recs]);
  const matches = (rec) => !filter
    || (filter.kind === 'measure' ? rec.measure_type === filter.id : rec.risk === filter.id);
  const matchedCount = recs.filter(matches).length;

  const fcTone = bd.current_fc_pct == null ? undefined
    : bd.current_fc_pct < 25 ? 'ok' : bd.current_fc_pct < 30 ? 'warn' : 'bad';
  const metricItems = [
    { label: 'Себестоимость порции', value: money(bd.current_cost_rub) },
    { label: 'Цена продажи', value: money(bd.sale_price_rub) },
    { label: 'Food cost', value: pct1(bd.current_fc_pct), tone: fcTone },
    bd.target_fc_pct != null
      // разрыв — в подпись (короткое значение не переносится на мобильной 2×2)
      ? { label: `Цель${bd.gap_rub != null ? ` · разрыв ${money(bd.gap_rub)}` : ''}`, value: pct1(bd.target_fc_pct), tone: 'info' }
      : { label: 'Выход порции', value: grams(bd.output_g) },
  ];

  return (
    <>
      <div className="canvas-metrics" style={{ '--cm-count': metricItems.length }}>
        {metricItems.map((m, i) => (
          <div className={'canvas-metric' + (m.tone ? ' canvas-metric--' + m.tone : '')} key={i}>
            <span className="canvas-metric__val num">{m.value}</span>
            <span className="canvas-metric__lbl t-caption">{m.label}</span>
          </div>
        ))}
      </div>

      <AiSkippedNote r={r} />
      <ConstraintsPanel c={r.constraints} compact={compact} />
      <CardsPanel meta={r.meta} compact={compact} />

      <Sec
        title="Драйверы себестоимости"
        hint={compact ? null : 'вклад в порцию с раскрытием полуфабрикатов — наведите для цепочки'}
      >
        <Drivers list={r.drivers} compact={compact} />
      </Sec>

      {!compact && (r.components || []).length > 0 && (
        <Sec title="Состав блюда" hint="строки карты верхнего уровня">
          <ComponentsTable rows={r.components} />
        </Sec>
      )}

      {(r.scenarios || []).length > 0 && (
        <Sec title="Допустимые сценарии">
          <Scenarios list={r.scenarios} />
        </Sec>
      )}

      {recs.length > 0 && (
        <Sec title={`Рекомендации (${recs.length})`} hint="клик по тегу фильтрует список">
          <div className="cv-chips">
            {FC_MEASURE_ORDER.filter((id) => counts.measure[id]).map((id) => (
              <FilterTag
                key={id} meta={fcMeasure(id)}
                active={!!filter && filter.kind === 'measure' && filter.id === id}
                onToggle={() => toggle('measure', id)}
                count={counts.measure[id]}
              />
            ))}
            {FC_RISK_ORDER.filter((id) => counts.risk[id]).map((id) => (
              <FilterTag
                key={id} meta={fcRisk(id)}
                active={!!filter && filter.kind === 'risk' && filter.id === id}
                onToggle={() => toggle('risk', id)}
                count={counts.risk[id]}
              />
            ))}
          </div>
          {filter && (
            <p className="t-caption text-mute inv2-filternote">
              Фильтр: «{(filter.kind === 'measure' ? fcMeasure(filter.id) : fcRisk(filter.id)).label}»
              {' '}— {matchedCount} из {recs.length}.{' '}
              <button type="button" className="inv2-reset" onClick={() => setFilter(null)}>Сбросить</button>
            </p>
          )}
          <div className="cv-measures cv-measures--doc">
            {recs.map((rec) => (
              <RecCard
                key={rec.id} rec={rec} filter={filter} onToggle={toggle}
                dimmed={!matches(rec)}
              />
            ))}
          </div>
        </Sec>
      )}

      {recs.length === 0 && r.verdict && r.verdict.guidance && (
        <div className="callout callout--info inv2-callout" role="note">
          <Icon name="info" size={15} />
          <div>
            <strong>Конкретные меры не сформированы.</strong> {r.verdict.guidance}
          </div>
        </div>
      )}

      {!compact && r.variants && (
        <Sec title="Лучшие варианты" hint="состав мер — по версии аналитика, числа посчитаны кодом">
          <Variants variants={r.variants} recs={recs} picked={(r.final || {}).variant} />
        </Sec>
      )}

      {r.final && (
        <Sec title="Финальный расчёт">
          <FinalTable final={r.final} breakdown={bd} />
        </Sec>
      )}

      {r.verdict && (r.verdict.implement_first || r.verdict.main_effect || r.verdict.main_risk) && (
        <Sec title="Итог">
          <ul className="cv-note-list cv-note-list--ok">
            {r.verdict.implement_first && (
              <li><Icon name="check-circle" size={13} /><span className="text-dim"><strong>Первым:</strong> {r.verdict.implement_first}</span></li>
            )}
            {r.verdict.main_effect && (
              <li><Icon name="analytics" size={13} /><span className="text-dim"><strong>Основной эффект:</strong> {r.verdict.main_effect}</span></li>
            )}
            {r.verdict.main_risk && (
              <li><Icon name="alert-triangle" size={13} /><span className="text-dim"><strong>Основной риск:</strong> {r.verdict.main_risk}</span></li>
            )}
          </ul>
        </Sec>
      )}

      {(r.assumptions || []).length > 0 && !compact && (
        <Sec title="Допущения">
          <ul className="cv-note-list">
            {r.assumptions.map((s, i) => (
              <li key={i}><Icon name="info" size={13} /><span className="text-dim">{s}</span></li>
            ))}
          </ul>
        </Sec>
      )}

      {(r.clarifications || []).length > 0 && (
        <div className="callout callout--info inv2-callout" role="note">
          <Icon name="info" size={15} />
          <div>{r.clarifications.map((s, i) => <div key={i}>{s}</div>)}</div>
        </div>
      )}

      {r.summary && (
        <div className="cv-callout report-verdict"><Icon name="sparkles" size={18} /><p>{r.summary}</p></div>
      )}
    </>
  );
}

export { FoodcostOptimizationReport };
